// Freies Malen auf Waggons und Hofmauern (Konzept §6.3 / §7).
//
// Harte Vorgabe: „einfache Graffiti-Funktion ohne resource drain".
// Im Leerlauf kostet dieser Code pro Frame *null* außer einem gedrosselten
// Raycast, und den nur mit Dose in der Hand. Keine Signaturen, keine Zähler,
// kein Autor — Konzept §8: sobald eine Zahl steht, ist das Territorialspiel gebaut.

import {
  PAINT_REACH,
  PAINT_MIN_POINT_DISTANCE,
  PAINT_MAX_POINTS_PER_STROKE,
  PAINT_SPRAY_RATE,
  PAINT_CAN_CAPACITY,
  PAINT_COLORS,
  PAINT_SURFACE_PX_PER_M,
  PAINT_MAX_TEXTURE,
} from '../../shared/constants.js';
import { STATIC_PAINT_SURFACES } from '../../shared/consist.js';
import { SurfaceManager, clampStrokePts, decimatePoints } from './canvas.js';

const RAY_HZ = 10;          // Zielen: 10×/s reicht für den HUD-Prompt
const MAINT_HZ = 2;         // Abo / Distanz / LRU
const SUBSCRIBE_RADIUS = 90;
const BRUSH_MIN = 0.012;
const BRUSH_MAX = 0.14;
const BRUSH_DEFAULT = 0.04;

/** Wiederverwendet von decimatePoints — nichts pro Frame neu. */
const _decimateBuf = [];

export class PaintSystem {
  constructor() {
    this.surfaces = new SurfaceManager();
    this.canOut = false;
    this.canFill = PAINT_CAN_CAPACITY;
    this.colorIndex = 0;
    this.color = PAINT_COLORS[0];
    this.brushWidth = BRUSH_DEFAULT;
    this.tool = 'spray'; // 'spray' | 'marker'

    /** @type {null | { surfaceId: string, u: number, v: number, widthM: number, heightM: number, distance: number }} */
    this.target = null;

    this._active = null;
    this._rayWait = 0;
    this._maintWait = 0;
    this._raycaster = null;
    this._ndc = null;
    this._invMat = null;
    this._vWorld = null;
    this._vLocal = null;
    this._hitMeshes = [];
    this._distMap = new Map();
    this._subscribed = new Set();
    this._netBound = false;
    this._lastCtx = null;
  }

  /** HUD/Debug-Kompatibilität: Dose in der Hand (Feldname war parallel anders benannt). */
  get holdingCan() { return this.canOut; }
  get hasCan() { return this.canOut; }
  get toolActive() { return this.canOut; }
  get spraying() { return !!this._active; }
  get painting() { return !!this._active; }
  get liveTextures() { return this.surfaces.liveTextureCount(); }
  get textureCount() { return this.surfaces.liveTextureCount(); }
  get colors() { return PAINT_COLORS; }

  async init(ctx) {
    const THREE = ctx.THREE;
    this._raycaster = new THREE.Raycaster();
    // Nur Bildmitte — Fadenkreuz; kein freies Pointer-Tracking nötig.
    this._ndc = new THREE.Vector2(0, 0);
    this._invMat = new THREE.Matrix4();
    this._vWorld = new THREE.Vector3();
    this._vLocal = new THREE.Vector3();

    ctx.paintTarget = null;

    if (ctx.net && !this._netBound) {
      this._netBound = true;
      ctx.net.on('paintData', (surfaceId, strokes) => {
        this.#onPaintData(surfaceId, strokes);
      });
      ctx.net.on('paintAdd', (stroke) => {
        this.#onPaintAdd(stroke);
      });
      // Schreibrecht entzogen: offenen Strich abbrechen, nichts optimistisch
      // hinterlassen, das der Server sowieso verwirft (§13).
      ctx.net.on('access', (canWrite) => {
        if (!canWrite && this._active) this.#endStroke(this._lastCtx, { skipSend: true });
      });
      ctx.net.on('error', (code) => {
        if ((code === 'no_write' || code === 'banned') && this._active) {
          this.#endStroke(this._lastCtx, { skipSend: true });
        }
      });
    }
  }

  update(dt, ctx) {
    if (!ctx.started) return;
    this._lastCtx = ctx;

    const input = ctx.input;

    // Dose holen / weglegen.
    if (input.justPressed('paintTool')) {
      this.canOut = !this.canOut;
      if (!this.canOut) {
        this.#endStroke(ctx);
        this.target = null;
        ctx.paintTarget = null;
      }
    }

    if (!this.canOut) {
      // Ohne Dose: null Arbeit außer seltener Auslagerung.
      this._maintWait += dt;
      if (this._maintWait >= 1 / MAINT_HZ) {
        this._maintWait = 0;
        this.#maintain(ctx, null);
      }
      return;
    }

    // Farbe 1–0.
    for (let i = 0; i < 10; i++) {
      const code = i === 9 ? 'Digit0' : `Digit${i + 1}`;
      if (input.keyPressed(code)) {
        this.colorIndex = i;
        this.color = PAINT_COLORS[i];
      }
    }

    // Strichstärke Q/W — justPressed, damit Gehen mit W die Stärke nicht hochjagt.
    if (input.justPressed('brushUp')) {
      this.brushWidth = Math.min(BRUSH_MAX, this.brushWidth * 1.18);
    }
    if (input.justPressed('brushDown')) {
      this.brushWidth = Math.max(BRUSH_MIN, this.brushWidth / 1.18);
    }

    // Mausrad: Werkzeugcharakter (spray ↔ marker), Konzept §14.
    if (input.mouse.wheel) {
      this.tool = this.tool === 'spray' ? 'marker' : 'spray';
    }

    // Ohne Schreibrecht darf die Dose raus, aber es landet nichts (§13:
    // Lesen frei, Schreiben nicht). HUD erklärt das einmal unaufdringlich.
    const mayWrite = ctx.net?.canWrite !== false;
    const spraying = input.mouse.left && this.canFill > 0 && mayWrite;

    if (input.mouse.left && this.canFill > 0 && !mayWrite) {
      // Versuch zu malen ohne Recht: Flag für HUD, kein optimistischer Strich.
      if (ctx.net) ctx.net.writeDeniedOnce = true;
    }

    if (spraying) {
      // Beim Sprühen jeden Frame zielen — sonst lückenhaft.
      this.#raycast(ctx);
      this.#spray(dt, ctx);
    } else {
      if (this._active) this.#endStroke(ctx);
      this._rayWait += dt;
      if (this._rayWait >= 1 / RAY_HZ) {
        this._rayWait = 0;
        this.#raycast(ctx);
      }
    }

    this._maintWait += dt;
    if (this._maintWait >= 1 / MAINT_HZ) {
      this._maintWait = 0;
      this.#maintain(ctx, this._active?.surfaceId ?? this.target?.surfaceId ?? null);
    }
  }

  /**
   * Offenen Strich abschließen.
   * main.js ruft das bei beforeunload — Connection puffert offline schon.
   */
  flushPending() {
    if (this._active && this._lastCtx) this.#endStroke(this._lastCtx);
  }

  dispose() {
    this.surfaces.dispose();
    this._hitMeshes.length = 0;
    this._distMap.clear();
  }

  // --- Zielen ---------------------------------------------------------------

  #raycast(ctx) {
    const camera = ctx.camera;
    if (!camera || !this._raycaster) {
      this.target = null;
      ctx.paintTarget = null;
      return;
    }

    this.#collectMeshes(ctx);
    if (this._hitMeshes.length === 0) {
      this.target = null;
      ctx.paintTarget = null;
      return;
    }

    this._raycaster.setFromCamera(this._ndc, camera);
    this._raycaster.far = PAINT_REACH + 0.5;
    const hits = this._raycaster.intersectObjects(this._hitMeshes, false);

    let best = null;
    for (let i = 0; i < hits.length; i++) {
      const h = hits[i];
      if (h.distance > PAINT_REACH) break;
      const mesh = h.object;
      const surfaceId = mesh.userData?.surfaceId;
      if (!surfaceId) continue;
      const uv = this.#hitToUV(mesh, h);
      if (!uv) continue;
      const meta = this.surfaces.get(surfaceId);
      best = {
        surfaceId,
        u: uv.u,
        v: uv.v,
        widthM: meta?.widthM ?? mesh.userData.widthM ?? 1,
        heightM: meta?.heightM ?? mesh.userData.heightM ?? 1,
        mesh,
        distance: h.distance,
      };
      break;
    }

    this.target = best;
    // HUD-Prompt — nur Fläche, keine Stats (Konzept §8).
    ctx.paintTarget = best
      ? { surfaceId: best.surfaceId, distance: best.distance }
      : null;
  }

  /**
   * Lokale Trefferposition → normierte (u,v).
   * Bewusst ohne Geometrie-UVs: funktioniert auf Boxen und Planes gleich,
   * unabhängig davon, wie train.js die Waggon-Meshes baut.
   */
  #hitToUV(mesh, hit) {
    this._vWorld.copy(hit.point);
    this._invMat.copy(mesh.matrixWorld).invert();
    this._vLocal.copy(this._vWorld).applyMatrix4(this._invMat);

    const geo = mesh.geometry;
    if (!geo) return null;
    if (!geo.boundingBox) geo.computeBoundingBox();
    const bb = geo.boundingBox;
    if (!bb) return null;

    let nx, ny, nz;
    if (hit.face && hit.face.normal) {
      // face.normal liegt im Lokalraum der Geometrie.
      nx = hit.face.normal.x;
      ny = hit.face.normal.y;
      nz = hit.face.normal.z;
    } else {
      // Ohne Face: dünnste Bounding-Achse ≈ Flächennormale.
      const sx = bb.max.x - bb.min.x;
      const sy = bb.max.y - bb.min.y;
      const sz = bb.max.z - bb.min.z;
      if (sx <= sy && sx <= sz) { nx = 1; ny = 0; nz = 0; }
      else if (sz <= sy) { nx = 0; ny = 0; nz = 1; }
      else { nx = 0; ny = 1; nz = 0; }
    }

    const ax = Math.abs(nx), ay = Math.abs(ny), az = Math.abs(nz);
    const lx = this._vLocal.x, ly = this._vLocal.y, lz = this._vLocal.z;
    const eps = 1e-6;

    let u, v;
    if (ax >= ay && ax >= az) {
      // ±X-Wand: u entlang Z, v entlang Y
      const zw = bb.max.z - bb.min.z || eps;
      const yw = bb.max.y - bb.min.y || eps;
      u = (lz - bb.min.z) / zw;
      v = (ly - bb.min.y) / yw;
      if (nx < 0) u = 1 - u;
    } else if (az >= ay) {
      // ±Z-Wand: u entlang X, v entlang Y
      const xw = bb.max.x - bb.min.x || eps;
      const yw = bb.max.y - bb.min.y || eps;
      u = (lx - bb.min.x) / xw;
      v = (ly - bb.min.y) / yw;
      if (nz > 0) u = 1 - u;
    } else {
      // ±Y (Decke/Boden — selten, aber absichern)
      const xw = bb.max.x - bb.min.x || eps;
      const zw = bb.max.z - bb.min.z || eps;
      u = (lx - bb.min.x) / xw;
      v = (lz - bb.min.z) / zw;
      if (ny < 0) v = 1 - v;
    }

    // Optionaler Override von train/station, falls die Achsen verdreht sind.
    const map = mesh.userData?.paintMap;
    if (typeof map === 'function') {
      const o = map(lx, ly, lz, bb);
      if (o && Number.isFinite(o.u) && Number.isFinite(o.v)) {
        u = o.u;
        v = o.v;
      }
    }

    u = u < 0 ? 0 : u > 1 ? 1 : u;
    v = v < 0 ? 0 : v > 1 ? 1 : v;
    return { u, v };
  }

  #collectMeshes(ctx) {
    this._hitMeshes.length = 0;
    const train = ctx.train;
    if (train?.cars) {
      for (let i = 0; i < train.cars.length; i++) {
        const car = train.cars[i];
        const surfaces = car.paintSurfaces;
        if (!surfaces) continue;
        for (let j = 0; j < surfaces.length; j++) {
          const s = surfaces[j];
          if (!s.mesh) continue;
          this.surfaces.register({
            surfaceId: s.surfaceId,
            pxW: s.pxW,
            pxH: s.pxH,
            widthM: s.widthM,
            heightM: s.heightM,
            mesh: s.mesh,
          });
          s.mesh.userData.surfaceId = s.surfaceId;
          s.mesh.userData.widthM = s.widthM;
          s.mesh.userData.heightM = s.heightM;
          this._hitMeshes.push(s.mesh);
        }
      }
    }

    // Station: paintMeshes ist eine Map (station.js), nicht ein Array —
    // Array-Indexierung würde still alle Hofmauern/Türme überspringen.
    const station = ctx.systems?.station;
    const staticSrc = station?.paintMeshes ?? station?.staticPaintMeshes ?? null;
    if (staticSrc) {
      const iter = staticSrc instanceof Map
        ? staticSrc.values()
        : (Array.isArray(staticSrc) || typeof staticSrc[Symbol.iterator] === 'function'
          ? staticSrc
          : null);
      if (iter) {
        for (const mesh of iter) {
          this.#registerStaticMesh(mesh);
        }
      }
    }

    // Abgestellter Waggon am toten Nebengleis (track_mesh.js) — eigene surfaceId,
    // nicht in STATIC_PAINT_SURFACES, muss trotzdem raycastbar sein.
    const dead = ctx.systems?.track?.deadCar;
    if (dead?.children) {
      for (let i = 0; i < dead.children.length; i++) {
        this.#registerStaticMesh(dead.children[i]);
      }
    }
  }

  #registerStaticMesh(mesh) {
    const id = mesh?.userData?.surfaceId;
    if (!id) return;
    const def = STATIC_PAINT_SURFACES.find((s) => s.surfaceId === id);
    const ps = mesh.userData.paintSurface;
    const widthM = def?.widthM ?? ps?.widthM ?? mesh.userData.widthM ?? 1;
    const heightM = def?.heightM ?? ps?.heightM ?? mesh.userData.heightM ?? 1;
    const pxW = def
      ? Math.min(PAINT_MAX_TEXTURE, Math.round(def.widthM * PAINT_SURFACE_PX_PER_M))
      : (mesh.userData.pxW || Math.min(PAINT_MAX_TEXTURE, Math.round(widthM * PAINT_SURFACE_PX_PER_M)));
    const pxH = def
      ? Math.min(PAINT_MAX_TEXTURE, Math.round(def.heightM * PAINT_SURFACE_PX_PER_M))
      : (mesh.userData.pxH || Math.min(PAINT_MAX_TEXTURE, Math.round(heightM * PAINT_SURFACE_PX_PER_M)));
    mesh.userData.widthM = widthM;
    mesh.userData.heightM = heightM;
    this.surfaces.register({
      surfaceId: id,
      pxW,
      pxH,
      widthM,
      heightM,
      mesh,
    });
    this._hitMeshes.push(mesh);
  }

  // --- Sprühen --------------------------------------------------------------

  #spray(dt, ctx) {
    const t = this.target;
    if (!t) {
      if (this._active) this.#endStroke(ctx);
      return;
    }

    // Flächenwechsel beendet den Strich — sonst Diagonalstrich durch die Welt.
    if (this._active && this._active.surfaceId !== t.surfaceId) {
      this.#endStroke(ctx);
    }

    if (!this._active) {
      // Pin während des Strichs: LRU darf die Fläche unter den Händen nicht kicken.
      this.surfaces.pinnedId = t.surfaceId;
      this.surfaces.ensureForPainting(t.surfaceId, true);
      this._active = {
        surfaceId: t.surfaceId,
        color: this.color,
        width: this.brushWidth,
        tool: this.tool,
        pts: [],
        widthM: t.widthM,
        heightM: t.heightM,
      };
    }

    const a = this._active;
    const pts = a.pts;
    const u = t.u, v = t.v;

    if (pts.length >= 2) {
      const lu = pts[pts.length - 2], lv = pts[pts.length - 1];
      const du = (u - lu) * a.widthM;
      const dv = (v - lv) * a.heightM;
      if (du * du + dv * dv < PAINT_MIN_POINT_DISTANCE * PAINT_MIN_POINT_DISTANCE) {
        // Zu nah — trotzdem Farbe verbrauchen (Halten = Sprühen).
        this.#consume(dt, a.width);
        return;
      }
    }

    pts.push(u, v);
    this.#consume(dt, a.width);

    if (pts.length / 2 >= PAINT_MAX_POINTS_PER_STROKE) {
      this.#endStroke(ctx);
    }
  }

  #consume(dt, width) {
    // Breite skaliert Verbrauch: dicke Tags leeren die Dose schneller.
    const scale = 0.55 + (width / BRUSH_DEFAULT) * 0.45;
    this.canFill -= PAINT_SPRAY_RATE * dt * scale;
    if (this.canFill <= 0) {
      // Sofort neue Dose — Vorräte verwalten wäre die verbotene Wartungsschleife (§11.1).
      // Der Trade-off bleibt die Zeit, die das Sprühen kostet (§7).
      this.canFill = PAINT_CAN_CAPACITY;
    }
  }

  /**
   * @param {object} [ctx]
   * @param {{ skipSend?: boolean }} [opts]  skipSend: bei Entzug des Schreibrechts
   *   keinen optimistischen lokalen Strich und keinen Netz-Versand.
   */
  #endStroke(ctx, opts = {}) {
    const a = this._active;
    this._active = null;
    if (this.surfaces.pinnedId && (!a || this.surfaces.pinnedId === a.surfaceId)) {
      this.surfaces.pinnedId = null;
    }
    if (!a || a.pts.length < 2) return;
    if (opts.skipSend) return;

    clampStrokePts(a.pts);
    decimatePoints(a.pts, a.widthM, a.heightM, PAINT_MIN_POINT_DISTANCE, _decimateBuf);
    if (_decimateBuf.length < 2) return;

    // Kopie: _decimateBuf wird wiederverwendet.
    const pts = _decimateBuf.slice();
    const stroke = {
      surfaceId: a.surfaceId,
      color: a.color,
      width: a.width,
      pts,
      tool: a.tool,
      worldSeconds: ctx?.worldSeconds ?? 0,
    };

    // Optimistisch lokal — Server schickt den eigenen Strich nicht zurück
    // (server/index.js: `if (other === session) continue`).
    // Herkunft `at` setzt nur der Server; hier nicht mitschicken und nicht
    // anzeigen (§4/§8 — nur Rohdaten für spätere Verwitterungsdarstellung).
    this.surfaces.addStroke(a.surfaceId, {
      s: a.surfaceId,
      c: a.color,
      w: a.width,
      pts,
      tl: a.tool,
      ws: stroke.worldSeconds,
    });

    ctx?.net?.sendStroke?.(stroke);
  }

  // --- Netz -----------------------------------------------------------------

  #onPaintData(surfaceId, strokes) {
    if (!this.surfaces.get(surfaceId)) {
      this.surfaces.register({ surfaceId, pxW: 256, pxH: 128 });
    }
    // Ein Durchgang, ein Textur-Update (SurfaceManager.setStrokes).
    this.surfaces.setStrokes(surfaceId, normalizeIncoming(strokes));
  }

  #onPaintAdd(stroke) {
    if (!stroke?.s || !stroke.pts) return;
    if (!this.surfaces.get(stroke.s)) {
      this.surfaces.register({ surfaceId: stroke.s, pxW: 256, pxH: 128 });
    }
    this.surfaces.addStroke(stroke.s, {
      s: stroke.s,
      c: stroke.c,
      w: stroke.w,
      pts: stroke.pts,
      tl: stroke.tl,
      ws: stroke.ws,
    });
  }

  #maintain(ctx, keepId) {
    const player = ctx.playerPos ?? ctx.playerState?.pos;
    if (!player) return;

    this._distMap.clear();
    const toSub = [];

    const train = ctx.train;
    if (train?.cars) {
      for (let i = 0; i < train.cars.length; i++) {
        const car = train.cars[i];
        let cx = player.x, cy = player.y, cz = player.z;
        if (car.group?.position) {
          cx = car.group.position.x;
          cy = car.group.position.y;
          cz = car.group.position.z;
        } else if (typeof train.localToWorld === 'function' && this._vLocal && this._vWorld) {
          this._vLocal.set(0, 1.5, 0);
          train.localToWorld(car.id, this._vLocal, this._vWorld);
          cx = this._vWorld.x; cy = this._vWorld.y; cz = this._vWorld.z;
        }
        const dist = Math.hypot(cx - player.x, cy - player.y, cz - player.z);
        const surfaces = car.paintSurfaces;
        if (!surfaces) continue;
        for (let j = 0; j < surfaces.length; j++) {
          const s = surfaces[j];
          this.surfaces.register({
            surfaceId: s.surfaceId,
            pxW: s.pxW,
            pxH: s.pxH,
            widthM: s.widthM,
            heightM: s.heightM,
            mesh: s.mesh,
          });
          this._distMap.set(s.surfaceId, dist);
          if (dist < SUBSCRIBE_RADIUS) toSub.push(s.surfaceId);
        }
      }
    }

    for (const def of STATIC_PAINT_SURFACES) {
      const e = this.surfaces.get(def.surfaceId);
      const mesh = e?.mesh
        ?? ctx.systems?.station?.paintMeshes?.get?.(def.surfaceId)
        ?? null;
      let dist = e?.distM ?? Infinity;
      if (mesh?.getWorldPosition && this._vWorld) {
        mesh.getWorldPosition(this._vWorld);
        dist = Math.hypot(
          this._vWorld.x - player.x,
          this._vWorld.y - player.y,
          this._vWorld.z - player.z
        );
      }
      // Auch unregistrierte Statik mit Default-Meta bekannt machen.
      if (!e) {
        this.surfaces.register({
          surfaceId: def.surfaceId,
          pxW: Math.min(PAINT_MAX_TEXTURE, Math.round(def.widthM * PAINT_SURFACE_PX_PER_M)),
          pxH: Math.min(PAINT_MAX_TEXTURE, Math.round(def.heightM * PAINT_SURFACE_PX_PER_M)),
          widthM: def.widthM,
          heightM: def.heightM,
          mesh: mesh || undefined,
        });
      } else if (mesh && !e.mesh) {
        e.mesh = mesh;
      }
      this._distMap.set(def.surfaceId, dist);
      if (dist < SUBSCRIBE_RADIUS) toSub.push(def.surfaceId);
    }

    // Toter Nebengleis-Waggon: Abo + Distanz, sonst nie paintData.
    const dead = ctx.systems?.track?.deadCar;
    if (dead) {
      let ddx = player.x, ddy = player.y, ddz = player.z;
      if (dead.position) {
        ddx = dead.position.x;
        ddy = dead.position.y;
        ddz = dead.position.z;
      }
      const deadDist = Math.hypot(ddx - player.x, ddy - player.y, ddz - player.z);
      const kids = dead.children;
      if (kids) {
        for (let i = 0; i < kids.length; i++) {
          const mesh = kids[i];
          const id = mesh?.userData?.surfaceId;
          if (!id) continue;
          const ps = mesh.userData.paintSurface;
          this.surfaces.register({
            surfaceId: id,
            pxW: Math.min(PAINT_MAX_TEXTURE, Math.round((ps?.widthM ?? 10) * PAINT_SURFACE_PX_PER_M)),
            pxH: Math.min(PAINT_MAX_TEXTURE, Math.round((ps?.heightM ?? 2.4) * PAINT_SURFACE_PX_PER_M)),
            widthM: ps?.widthM ?? mesh.userData.widthM ?? 10,
            heightM: ps?.heightM ?? mesh.userData.heightM ?? 2.4,
            mesh,
          });
          this._distMap.set(id, deadDist);
          if (deadDist < SUBSCRIBE_RADIUS) toSub.push(id);
        }
      }
    }

    if (toSub.length && ctx.net?.subscribe) {
      const fresh = [];
      for (let i = 0; i < toSub.length; i++) {
        const id = toSub[i];
        if (this._subscribed.has(id)) continue;
        this._subscribed.add(id);
        fresh.push(id);
      }
      if (fresh.length) ctx.net.subscribe(fresh);
    }

    this.surfaces.maintain(this._distMap, keepId);
  }
}

function normalizeIncoming(strokes) {
  if (!Array.isArray(strokes)) return [];
  const out = new Array(strokes.length);
  for (let i = 0; i < strokes.length; i++) {
    const s = strokes[i];
    out[i] = {
      s: s.s,
      c: s.c,
      w: s.w,
      pts: Array.isArray(s.pts) ? s.pts : [],
      tl: s.tl === 'marker' ? 'marker' : 'spray',
      ws: s.ws,
    };
  }
  return out;
}
